import { useMemo } from 'react';
import type { FirestoreError } from 'firebase/firestore';
import type { CatalogDesignation, CatalogSection } from '@altyor-kronos/shared-types';
import { usePolledResource } from '../../core/data/usePolledResource';
import { apiList } from '../../core/data/restClient';

export interface CatalogRow {
  id: string;
  data: CatalogDesignation;
}

export interface CatalogState {
  entries: CatalogRow[];
  loading: boolean;
  error: FirestoreError | null;
}

/**
 * Read active (non-archived) catalog entries for a given section.
 * Used by the project DesignationDialog to populate the smart picker.
 */
export function useCatalogSnapshot(section: CatalogSection): CatalogState {
  const { rows, loading, error } = usePolledResource<CatalogDesignation & { id: string }>(
    'catalog',
    () => apiList<CatalogDesignation & { id: string }>('/designationCatalog'),
  );
  // Server list is unfiltered — apply section + archived filter + order sort client-side.
  const entries = useMemo(
    () =>
      rows
        .filter((r) => r.section === section && !r.isArchived)
        // `order` is a Firestore field not declared on CatalogDesignation.
        .sort((a, b) => ((a as { order?: number }).order ?? 0) - ((b as { order?: number }).order ?? 0))
        .map((r) => ({ id: r.id, data: r })),
    [rows, section],
  );
  return { entries, loading, error: error as FirestoreError | null };
}
